import { type ColorOption, ColorTone, type Manufacturer } from './colorOptions'

/**
 * 検索ワード（色コード or 色名の部分一致、case-insensitive）にマッチするか判定.
 */
export const matchesColorSearch = (color: ColorOption, searchTerm: string): boolean => {
  if (!searchTerm) return true
  const term = searchTerm.toLowerCase()
  return color.value.toLowerCase().includes(term) || color.label.toLowerCase().includes(term)
}

interface FilterColorsOptions {
  /** 指定メーカーに一致する色のみ。null の場合は全メーカーが対象 */
  manufacturer?: Manufacturer | null
  /** 指定カラートーンに一致する色のみ。null / UNSPECIFIED の場合は全トーン */
  tone?: ColorTone | null
  /** 色コード or 色名の部分一致（case-insensitive） */
  searchTerm?: string
}

/**
 * 色一覧を「メーカー / カラートーン / 検索ワード」で絞り込む共通フィルタ.
 * 色指定パレット（ColorPalette）と企業プリセット管理（PresetEditor）で共通利用する.
 *
 * トーン判定は色マスターの `tone` をそのまま比較する。かつては旧 `category` 列を
 * トーンへ変換して比較していたが、`category` は本番で全レコード空文字だったため
 * どのトーンを選んでも 0 件になっていた（本 fix の発端）.
 */
export const filterColors = (
  colors: ColorOption[],
  options: FilterColorsOptions,
): ColorOption[] => {
  const { manufacturer = null, tone = null, searchTerm = '' } = options
  const toneFilter = tone === ColorTone.UNSPECIFIED ? null : tone
  return colors.filter(c => {
    const matchMaker = manufacturer === null || c.manufacturer === manufacturer
    const matchTone = toneFilter === null || c.tone === toneFilter
    return matchMaker && matchTone && matchesColorSearch(c, searchTerm)
  })
}

/**
 * 色一覧から重複を除いたメーカー一覧を返す（出現順）.
 */
export const extractManufacturers = (colors: ColorOption[]): Manufacturer[] => {
  const seen = new Set<Manufacturer>()
  const result: Manufacturer[] = []
  for (const c of colors) {
    if (!seen.has(c.manufacturer)) {
      seen.add(c.manufacturer)
      result.push(c.manufacturer)
    }
  }
  return result
}
